import { defineComponent, getCurrentInstance, provide, ref, onBeforeMount, onUnmounted, inject, computed } from 'vue';
import { getFallback, getTranslateProps } from '@tolgee/web';
export * from '@tolgee/web';

/* eslint-disable @typescript-eslint/no-var-requires */
const TolgeeProvider = defineComponent({
    name: 'TolgeeProvider',
    props: {
        tolgee: { type: Object, required: false },
        fallback: {
            type: [Object, String],
        },
    },
    setup(props) {
        const tolgee = props.tolgee || getCurrentInstance().proxy.$tolgee;
        if (!tolgee) {
            throw new Error('Tolgee instance not provided');
        }
        provide('tolgeeContext', { tolgee });
        const isLoading = ref(!tolgee.isLoaded());
        onBeforeMount(() => {
            tolgee.run().finally(() => {
                isLoading.value = false;
            });
        });
        onUnmounted(() => {
            tolgee.stop();
        });
        return { isLoading };
    },
    render() {
        var _a, _b, _c, _d;
        return !this.isLoading
            ? (_b = (_a = this.$slots).default) === null || _b === void 0 ? void 0 : _b.call(_a)
            : ((_d = (_c = this.$slots).fallback) === null || _d === void 0 ? void 0 : _d.call(_c)) || this.fallback || null;
    },
});

const useTranslateInternal = (ns) => {
    const namespaces = getFallback(ns);
    const tolgeeContext = inject('tolgeeContext', {
        tolgee: getCurrentInstance().proxy.$tolgee,
    });
    const tolgee = tolgeeContext === null || tolgeeContext === void 0 ? void 0 : tolgeeContext.tolgee;
    if (!tolgee) {
        throw new Error('Tolgee instance not provided');
    }
    const t = ref(createTFunction());
    const subscription = tolgee.onNsUpdate(() => {
        t.value = createTFunction();
        isLoading.value = !tolgee.isLoaded(namespaces);
    });
    subscription.subscribeNs(namespaces);
    tolgee.addActiveNs(namespaces);
    onUnmounted(() => {
        subscription === null || subscription === void 0 ? void 0 : subscription.unsubscribe();
        tolgee.removeActiveNs(namespaces);
    });
    const isLoading = ref(!tolgee.isLoaded(namespaces));
    const subscribeToNs = (ns) => {
        subscription.subscribeNs(ns);
    };
    function createTFunction() {
        return (props) => {
            var _a;
            const fallbackNs = (_a = props.ns) !== null && _a !== void 0 ? _a : namespaces === null || namespaces === void 0 ? void 0 : namespaces[0];
            subscribeToNs(fallbackNs);
            return tolgee.t(Object.assign(Object.assign({}, props), { ns: fallbackNs }));
        };
    }
    return { t, isLoading };
};

const T = defineComponent({
    name: 'T',
    props: {
        keyName: { type: String, required: true },
        params: Object,
        defaultValue: String,
        noWrap: {
            type: Boolean,
            default: false,
        },
        ns: { type: Object },
        language: { type: String },
    },
    setup() {
        const { t } = useTranslateInternal();
        return { t };
    },
    render() {
        const params = {
            key: this.$props.keyName,
            params: this.$props.params,
            defaultValue: this.$props.defaultValue,
            noWrap: this.$props.noWrap,
            ns: this.$props.ns,
            language: this.$props.language,
        };
        const content = this.t(params);
        return content;
    },
});

const useTolgee = (events) => {
    const instance = getCurrentInstance();
    const tolgeeContext = inject('tolgeeContext', {
        tolgee: instance.proxy.$tolgee,
    });
    const tolgee = ref(tolgeeContext.tolgee);
    const listeners = events === null || events === void 0 ? void 0 : events.map((e) => tolgee.value.on(e, () => {
        tolgee.value = Object.freeze(Object.assign({}, tolgee.value));
        instance.proxy.$forceUpdate();
    }));
    onUnmounted(() => {
        listeners === null || listeners === void 0 ? void 0 : listeners.forEach((listener) => listener.unsubscribe());
    });
    return tolgee;
};

const useTranslate = (namespaces) => {
    const { t: tInternal, isLoading } = useTranslateInternal(namespaces);
    const t = computed(() => (...params) => {
        // @ts-ignore
        const props = getTranslateProps(...params);
        return tInternal.value(props);
    });
    return { t, isLoading };
};

const GlobalContextPlugin = () => (tolgee) => {
    return tolgee;
};

const VueTolgee = {
    install(app, options) {
        const tolgee = options === null || options === void 0 ? void 0 : options.tolgee;
        if (!tolgee) {
            throw new Error('Tolgee instance not passed in options');
        }
        app.mixin({
            beforeCreate() {
                this.$options.__keySubscription = tolgee.onNsUpdate(() => {
                    this.$forceUpdate();
                });
            },
            unmounted() {
                this.$options.__keySubscription.unsubscribe();
            },
            methods: {
                // @ts-ignore
                $t(...props) {
                    // @ts-ignore
                    const params = getTranslateProps(...props);
                    const { ns } = params;
                    this.$options.__keySubscription.subscribeNs(ns);
                    return tolgee.t(params);
                },
            },
        });
        app.config.globalProperties.$tolgee = tolgee;
    },
};

export { GlobalContextPlugin, T, TolgeeProvider, VueTolgee, useTolgee, useTranslate };
//# sourceMappingURL=tolgee-vue.esm.mjs.map
